import { useState, useEffect } from 'react';
import { X, Search, Loader, CheckCircle2, Video, Power, PowerOff, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { logAction } from '../lib/actionLog';

export type CampaignPlatform = 'meta' | 'tiktok';

interface LinkReleaseCampaignModalProps {
  platform: CampaignPlatform;
  releaseId: string;
  releaseName: string | null;
  currentCampaignId: string | null;
  currentCampaignName: string | null;
  onClose: () => void;
  onSave: (campaignId: string, campaignName: string) => Promise<void>;
}

interface Campaign {
  campaign_id: string;
  campaign_name: string;
  status?: string;
  effective_status?: string;
  objective?: string;
}

const REPORTING_SUPABASE_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
const REPORTING_SUPABASE_ANON_KEY =
  'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';

const PLATFORM_LABEL: Record<CampaignPlatform, string> = {
  meta: 'Meta',
  tiktok: 'TikTok',
};

const PLATFORM_ACCENT: Record<CampaignPlatform, string> = {
  meta: 'blue',
  tiktok: 'pink',
};

// Module-level cooldown for the Meta auto-sync. When the Meta API returns a
// rate-limit error, we stamp `until = Date.now() + cooldown` so subsequent
// modal opens skip the auto-sync silently until the timestamp passes. Reset
// on full page reload (it's just in-memory).
const META_SYNC_RATE_LIMIT_COOLDOWN_MS = 10 * 60 * 1000;
let metaSyncRateLimitedUntil = 0;
const isMetaRateLimitError = (msg: string | undefined): boolean => {
  if (!msg) return false;
  const m = msg.toLowerCase();
  return (
    m.includes('too many calls') ||
    m.includes('rate limit') ||
    m.includes('rate-limit') ||
    m.includes('#4') || // Meta graph error subcode family for rate limits
    m.includes('(#17)') ||
    m.includes('(#32)') ||
    m.includes('(#613)')
  );
};

export function LinkReleaseCampaignModal({
  platform,
  releaseId,
  releaseName,
  currentCampaignId,
  currentCampaignName,
  onClose,
  onSave,
}: LinkReleaseCampaignModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tiktokTargeting, setTiktokTargeting] = useState<'dach' | 'international'>('dach');
  const [creatingTiktokCampaign, setCreatingTiktokCampaign] = useState(false);
  const [smartlinkUrl, setSmartlinkUrl] = useState<string | null>(null);
  const [smartlinkLoading, setSmartlinkLoading] = useState(false);
  const [smartlinkChecked, setSmartlinkChecked] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [manualSmartlinkUrl, setManualSmartlinkUrl] = useState<string | null>(null);
  const [smartlinkSearchQuery, setSmartlinkSearchQuery] = useState('');
  const [smartlinkSearching, setSmartlinkSearching] = useState(false);
  const [smartlinkSearchResults, setSmartlinkSearchResults] = useState<
    Array<{ smartlink_id: string; slug: string; artist_name: string; release_name: string; smartlink_url: string }>
  >([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    currentCampaignId
      ? {
          campaign_id: currentCampaignId,
          campaign_name: currentCampaignName || '',
        }
      : null,
  );

  // Smart+ ads (TikTok only, when already linked)
  const [smartPlusAds, setSmartPlusAds] = useState<any[] | null>(null);
  const [loadingSmartPlus, setLoadingSmartPlus] = useState(false);
  const [smartPlusError, setSmartPlusError] = useState<string | null>(null);

  // Meta thumbnail-based ad_creatives sync (auto-triggered when a Meta
  // campaign is already linked and this release has unmatched ad_creatives)
  const [metaSync, setMetaSync] = useState<{
    status: 'idle' | 'checking' | 'running' | 'done' | 'error' | 'nothing-to-do';
    unmatchedBefore?: number;
    matched?: number;
    unmatchedAfter?: number;
    error?: string;
    debug?: any;
  }>({ status: 'idle' });

  // Manual pairing state for the side-by-side matcher UI.
  // `manualPairs` holds user-proposed matches (via drag-and-drop) that haven't
  // been committed yet. Keyed by meta_ad_id for stable rendering.
  const [manualPairs, setManualPairs] = useState<
    Array<{ metaAdId: string; localId: string }>
  >([]);
  const [committingMetaAdId, setCommittingMetaAdId] = useState<string | null>(null);
  const [committedMetaAdIds, setCommittedMetaAdIds] = useState<Set<string>>(new Set());
  const [dragMetaAdId, setDragMetaAdId] = useState<string | null>(null);

  useEffect(() => {
    if (platform !== 'tiktok' || !currentCampaignId) return;
    let cancelled = false;
    setLoadingSmartPlus(true);
    setSmartPlusError(null);
    fetch(`${REPORTING_SUPABASE_URL}/functions/v1/get-tiktok-smart-plus-ads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ campaign_id: currentCampaignId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.error) {
          setSmartPlusError(data.message || data.error);
          setSmartPlusAds([]);
        } else {
          setSmartPlusAds(data.ads || []);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setSmartPlusError(err instanceof Error ? err.message : 'Failed to load Smart+ ads');
      })
      .finally(() => {
        if (!cancelled) setLoadingSmartPlus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [platform, currentCampaignId]);

  // Meta: auto-sync ad_creatives ↔ Meta ads by thumbnail when a campaign is
  // already linked and this release has ad_creatives without meta_ad_id.
  useEffect(() => {
    if (platform !== 'meta' || !currentCampaignId || !releaseId) {
      console.log('[meta-sync] effect skipped', { platform, currentCampaignId, releaseId });
      return;
    }
    // Respect the module-level rate-limit cooldown — re-opening the modal
    // within the cooldown window skips the auto-sync silently and surfaces
    // a "nothing-to-do" state so the UI doesn't look broken.
    if (Date.now() < metaSyncRateLimitedUntil) {
      const remainingMs = metaSyncRateLimitedUntil - Date.now();
      console.log(
        `[meta-sync] skipping auto-sync — Meta API cooldown active for ~${Math.round(remainingMs / 1000)}s more`,
      );
      setMetaSync({ status: 'nothing-to-do' });
      return;
    }
    let cancelled = false;
    const t0 = performance.now();
    console.log('[meta-sync] effect start', {
      platform,
      currentCampaignId,
      currentCampaignName,
      releaseId,
    });

    (async () => {
      setMetaSync({ status: 'checking' });
      try {
        // 1. Load this release's ad_creatives to check which are unmatched.
        //    Exclude TikTok-only creatives (no Instagram reel URL) — they can
        //    never appear in a Meta campaign.
        console.log('[meta-sync] loading ad_creatives for release', releaseId);
        const { data: rows, error: rowsErr } = await (supabase as any)
          .from('ad_creatives')
          .select('id, artists_id, meta_ad_id, merged_instagram_reel_url')
          .eq('release_id', releaseId)
          .not('merged_instagram_reel_url', 'is', null);
        if (cancelled) return;
        if (rowsErr) {
          console.error('[meta-sync] load failed', rowsErr);
          setMetaSync({ status: 'error', error: rowsErr.message });
          return;
        }
        const unmatched = (rows || []).filter((r: any) => !r.meta_ad_id);
        console.log('[meta-sync] loaded', {
          total: rows?.length || 0,
          unmatched: unmatched.length,
          already_matched: (rows?.length || 0) - unmatched.length,
        });
        if (unmatched.length === 0) {
          console.log('[meta-sync] nothing to do');
          setMetaSync({ status: 'nothing-to-do', unmatchedBefore: 0 });
          return;
        }
        const artistIds = Array.from(
          new Set(rows.map((r: any) => r.artists_id).filter(Boolean)),
        ) as string[];

        // 2. Fire sync
        console.log('[meta-sync] POST /sync-meta-campaign-creatives', {
          campaign_id: currentCampaignId,
          artist_ids: artistIds,
          unmatched_count: unmatched.length,
        });
        setMetaSync({ status: 'running', unmatchedBefore: unmatched.length });
        const reqStart = performance.now();
        const resp = await fetch(
          `${REPORTING_SUPABASE_URL}/functions/v1/sync-meta-campaign-creatives`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              campaign_id: currentCampaignId,
              artist_ids: artistIds.length > 0 ? artistIds : undefined,
            }),
          },
        );
        const reqMs = Math.round(performance.now() - reqStart);
        console.log(`[meta-sync] response received in ${reqMs}ms (status ${resp.status})`);
        const data = await resp.json();
        if (cancelled) return;
        if (!resp.ok || data?.error) {
          const errMsg = data?.error || data?.message || `HTTP ${resp.status}`;
          // Meta graph rate-limit: swallow silently and stamp the module
          // cooldown so subsequent opens don't hammer the API further.
          if (isMetaRateLimitError(errMsg)) {
            metaSyncRateLimitedUntil = Date.now() + META_SYNC_RATE_LIMIT_COOLDOWN_MS;
            console.warn(
              `[meta-sync] Meta API rate-limit hit — skipping auto-sync for ${Math.round(
                META_SYNC_RATE_LIMIT_COOLDOWN_MS / 1000,
              )}s`,
            );
            setMetaSync({ status: 'nothing-to-do', unmatchedBefore: unmatched.length });
            return;
          }
          console.error('[meta-sync] function returned error', data);
          setMetaSync({
            status: 'error',
            unmatchedBefore: unmatched.length,
            error: errMsg,
            debug: data,
          });
          return;
        }
        console.log('[meta-sync] function result', {
          method: data.comparison_method,
          threshold: data.similarity_threshold,
          meta_ads_total: data.meta_ads_total,
          meta_ads_hashed: data.meta_ads_hashed,
          meta_ads_preview_uploaded: data.meta_ads_preview_uploaded,
          local_creatives_total: data.local_creatives_total,
          local_creatives_preview_uploaded: data.local_creatives_preview_uploaded,
          matches_total: data.matches_total,
          matches_by_reason: data.matches_by_reason,
          updated_count: data.updated_count,
        });
        if (data.upload_errors?.length) {
          console.warn(
            `[meta-sync] ${data.upload_errors.length} upload error(s)`,
            data.upload_errors,
          );
        }
        if (data.cache_writeback_errors?.length) {
          console.error(
            `[meta-sync] ${data.cache_writeback_errors.length} cache writeback error(s)`,
          );
          // Print each as its own console line (flat) so the message is visible
          // without having to expand the array in devtools.
          for (const e of data.cache_writeback_errors as any[]) {
            console.error(`  · id=${e.id}: ${e.error}`);
          }
        }
        console.log(
          `[meta-sync] cache: ${data.cache_writeback_ok || 0} rows written`,
        );
        if (data.matches?.length) {
          console.log('[meta-sync] auto-matches:', data.matches);
        }
        if (data.unmatched_meta_ads?.length) {
          console.log(
            '[meta-sync] unmatched Meta ads (best-effort nearest):',
            data.unmatched_meta_ads.map((a: any) => ({
              ad_id: a.ad_id,
              ad_name: a.ad_name,
              nearest_local_id: a.nearest_local_id,
              nearest_distance_pct: a.nearest_distance,
            })),
          );
        }
        // 3. Re-check how many are still unmatched for this release (excluding
        //    TikTok-only creatives that can't appear in Meta).
        const { data: afterRows } = await (supabase as any)
          .from('ad_creatives')
          .select('id, meta_ad_id, merged_instagram_reel_url')
          .eq('release_id', releaseId)
          .not('merged_instagram_reel_url', 'is', null);
        if (cancelled) return;
        const unmatchedAfter = (afterRows || []).filter(
          (r: any) => !r.meta_ad_id,
        ).length;
        const matched = Math.max(0, unmatched.length - unmatchedAfter);
        const totalMs = Math.round(performance.now() - t0);
        console.log(`[meta-sync] done in ${totalMs}ms`, {
          unmatchedBefore: unmatched.length,
          unmatchedAfter,
          matched,
        });
        setMetaSync({
          status: 'done',
          unmatchedBefore: unmatched.length,
          unmatchedAfter,
          matched,
          debug: data,
        });
      } catch (err) {
        console.error('[meta-sync] exception', err);
        if (cancelled) return;
        setMetaSync({
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    })();

    return () => {
      cancelled = true;
      console.log('[meta-sync] effect cleanup');
    };
  }, [platform, currentCampaignId, currentCampaignName, releaseId]);

  // Commit a manually-paired Meta ad ↔ local ad_creative link to the DB.
  // Writes meta_ad_id + meta_campaign_id + meta_adset_id + status='active'.
  const commitMetaCreativeLink = async (metaAdId: string, localId: string) => {
    if (!currentCampaignId) return;
    const metaAd = metaSync.debug?.unmatched_meta_ads?.find(
      (a: any) => a.ad_id === metaAdId,
    );
    if (!metaAd) {
      console.error('[meta-sync] Meta ad data missing for', metaAdId);
      toast.error('Meta ad data missing, refresh the modal');
      return;
    }
    setCommittingMetaAdId(metaAdId);
    const payload: Record<string, unknown> = {
      meta_ad_id: metaAdId,
      meta_campaign_id: currentCampaignId,
      meta_adset_id: metaAd.adset_id || null,
      meta_ad_status: metaAd.effective_status || null,
      status: 'active',
      last_activated_at: new Date().toISOString(),
    };
    if (metaAd.video_id) payload.meta_video_id = metaAd.video_id;
    if (metaAd.image_hash) payload.meta_image_hash = metaAd.image_hash;
    console.groupCollapsed(
      `[meta-sync] commit ${metaAd.ad_name || metaAdId} → ${localId}`,
    );
    console.log('payload', payload);
    console.log('meta_ad_data', metaAd);
    const commitStart = performance.now();
    try {
      const { data, error: updErr } = await (supabase as any)
        .from('ad_creatives')
        .update(payload)
        .eq('id', localId)
        .select('id, meta_ad_id, meta_campaign_id, status');
      const commitMs = Math.round(performance.now() - commitStart);
      if (updErr) {
        console.error('update error', updErr);
        console.groupEnd();
        throw new Error(updErr.message);
      }
      console.log(`✓ result (${commitMs}ms)`, data);
      console.groupEnd();
      if (!data || data.length === 0) {
        throw new Error(
          `No rows updated for local id ${localId} (RLS block or missing id?)`,
        );
      }
      setCommittedMetaAdIds((prev) => {
        const next = new Set(prev);
        next.add(metaAdId);
        return next;
      });
      toast.success(`Linked ${metaAd.ad_name ? '#' + metaAd.ad_name : metaAdId}`);
    } catch (err) {
      console.error('[meta-sync] commit failed', err);
      toast.error(
        `Commit failed: ${err instanceof Error ? err.message : 'unknown'}`,
      );
    } finally {
      setCommittingMetaAdId(null);
    }
  };

  // Reset manual pairing state whenever the debug payload changes (new sync run).
  useEffect(() => {
    setManualPairs([]);
    setCommittedMetaAdIds(new Set());
  }, [metaSync.debug]);

  // Look up the song.so smartlink for this release (TikTok only, when no campaign linked)
  useEffect(() => {
    if (platform !== 'tiktok' || currentCampaignId || !releaseId) return;
    let cancelled = false;
    setSmartlinkLoading(true);
    setSmartlinkChecked(false);
    (async () => {
      try {
        const { data: release, error: releaseError } = await (supabase as any)
          .from('releases')
          .select('name, artists(name)')
          .eq('id', releaseId)
          .single();
        if (releaseError || !release) {
          if (!cancelled) {
            setSmartlinkUrl(null);
            setSmartlinkChecked(true);
          }
          return;
        }
        const artistsField = (release as any).artists;
        const artistName =
          (Array.isArray(artistsField) ? artistsField[0]?.name : artistsField?.name) || '';
        const releaseNameVal = (release as any).name || '';
        if (!artistName || !releaseNameVal) {
          if (!cancelled) {
            setSmartlinkUrl(null);
            setSmartlinkChecked(true);
          }
          return;
        }
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-songso-smartlink`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              artist_name: artistName,
              release_name: releaseNameVal,
            }),
          },
        );
        const data = await resp.json();
        if (cancelled) return;
        setSmartlinkUrl(data?.found && data?.smartlink_url ? data.smartlink_url : null);
      } catch (err) {
        console.error('Failed to fetch song.so smartlink:', err);
        if (!cancelled) setSmartlinkUrl(null);
      } finally {
        if (!cancelled) {
          setSmartlinkLoading(false);
          setSmartlinkChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [platform, currentCampaignId, releaseId]);

  // Debounced manual song.so search
  useEffect(() => {
    const q = smartlinkSearchQuery.trim();
    if (q.length < 2) {
      setSmartlinkSearchResults([]);
      setSmartlinkSearching(false);
      return;
    }
    let cancelled = false;
    setSmartlinkSearching(true);
    const t = setTimeout(async () => {
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-songso-smartlink`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ query: q }),
          },
        );
        const data = await resp.json();
        if (cancelled) return;
        setSmartlinkSearchResults(Array.isArray(data?.results) ? data.results : []);
      } catch (err) {
        console.error('song.so manual search failed:', err);
        if (!cancelled) setSmartlinkSearchResults([]);
      } finally {
        if (!cancelled) setSmartlinkSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [smartlinkSearchQuery]);

  // Prefill the search with the release name on open.
  useEffect(() => {
    if (releaseName && !searchQuery) {
      setSearchQuery(releaseName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchCampaigns = async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setCampaigns([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const endpoint =
        platform === 'meta' ? 'search-meta-campaigns' : 'search-tiktok-campaigns';
      const payload: any = { search_pattern: trimmed };
      if (platform === 'meta') payload.account_id = '626419741407621';

      const response = await fetch(`${REPORTING_SUPABASE_URL}/functions/v1/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.error || data.message || 'Search failed');
        setCampaigns([]);
      } else {
        // The two edge functions return different shapes:
        // search-meta-campaigns   → { id, campaign_id, campaign_name, ... }
        // search-tiktok-campaigns → { id, name, ... }
        // Normalise to campaign_id/campaign_name so the list renders and the
        // save payload is never built from undefined fields.
        const normalised: Campaign[] = (data.campaigns || [])
          .map((c: any) => ({
            ...c,
            campaign_id: String(c.campaign_id ?? c.id ?? ''),
            campaign_name: String(c.campaign_name ?? c.name ?? ''),
          }))
          .filter((c: Campaign) => !!c.campaign_id);
        setCampaigns(normalised);
      }
    } catch (err) {
      console.error('Error searching campaigns:', err);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) {
        searchCampaigns(searchQuery);
      }
    }, 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, platform]);

  const handleCreateTikTokCampaign = async () => {
    setCreatingTiktokCampaign(true);
    try {
      // Look up release info (artist name + release_date) for the edge function
      const { data: release, error: releaseError } = await (supabase as any)
        .from('releases')
        .select('id, name, release_date, artists(name)')
        .eq('id', releaseId)
        .single();

      if (releaseError || !release) {
        throw new Error(releaseError?.message || 'Release not found');
      }

      const artistsField = (release as any).artists;
      const artistName =
        (Array.isArray(artistsField) ? artistsField[0]?.name : artistsField?.name) ||
        'Unknown Artist';

      const response = await fetch(
        `${REPORTING_SUPABASE_URL}/functions/v1/create-tiktok-campaign-v3`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            release_id: release.id,
            artist_name: artistName,
            release_name: release.name,
            release_date: (release as any).release_date,
            targeting: tiktokTargeting,
            // Manual override wins; else backend will auto-resolve via song.so
            landing_page_url: manualSmartlinkUrl || undefined,
          }),
        },
      );

      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.details || data.error || 'Failed to create TikTok campaign');
      }

      toast.success('TikTok Smart+ Kampagne erstellt!');

      if (data.tiktok_ads_manager_url) {
        window.open(data.tiktok_ads_manager_url, '_blank');
      }

      // Auto-link the newly created campaign to the release
      if (data.campaign_id) {
        await onSave(String(data.campaign_id), String(data.campaign_name || ''));
      }
    } catch (err) {
      console.error('Error creating TikTok campaign:', err);
      toast.error(`Fehler: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    } finally {
      setCreatingTiktokCampaign(false);
    }
  };

  const handleUnlink = async () => {
    if (unlinking) return;
    setUnlinking(true);
    try {
      const updatePayload: Record<string, null> =
        platform === 'meta'
          ? { meta_campaign_id: null, meta_campaign_name: null }
          : {
              tiktok_campaign_id: null,
              tiktok_campaign_name: null,
              tiktok_adgroup_id: null,
              tiktok_ad_id: null,
            };
      const { error: updateErr } = await (supabase as any)
        .from('releases')
        .update(updatePayload)
        .eq('id', releaseId);
      if (updateErr) {
        toast.error(`Failed to unlink: ${updateErr.message}`);
        await logAction({
          entityType: 'release',
          entityId: releaseId,
          action: 'unlink_campaign',
          platform,
          status: 'error',
          details: { error: updateErr.message },
        });
        return;
      }
      await logAction({
        entityType: 'release',
        entityId: releaseId,
        action: 'unlink_campaign',
        platform,
        status: 'success',
        details: { previous_campaign_id: currentCampaignId },
      });
      toast.success(`${label} campaign unlinked`);
      onClose();
    } finally {
      setUnlinking(false);
    }
  };

  const handleSave = async () => {
    if (!selectedCampaign) return;
    if (!selectedCampaign.campaign_id) {
      toast.error('This campaign has no id — cannot link it');
      return;
    }
    setSaving(true);
    try {
      await onSave(selectedCampaign.campaign_id, selectedCampaign.campaign_name || '');
      await logAction({
        entityType: 'release',
        entityId: releaseId,
        action: 'link_campaign',
        platform,
        status: 'success',
        details: { campaign_id: selectedCampaign.campaign_id, campaign_name: selectedCampaign.campaign_name },
      });
    } catch (err) {
      await logAction({
        entityType: 'release',
        entityId: releaseId,
        action: 'link_campaign',
        platform,
        status: 'error',
        details: { error: err instanceof Error ? err.message : String(err) },
      });
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const accent = PLATFORM_ACCENT[platform];
  const label = PLATFORM_LABEL[platform];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white">
            {label} Campaign for "{releaseName}"
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 flex-1 overflow-y-auto">
          {currentCampaignId && (
            <div className={`bg-${accent}-50 dark:bg-${accent}-900/20 rounded-lg p-3 relative`}>
              <button
                type="button"
                onClick={handleUnlink}
                disabled={unlinking}
                title="Unlink this campaign from the release"
                className={`absolute top-2 right-2 p-1 rounded hover:bg-${accent}-100 dark:hover:bg-${accent}-800/30 text-${accent}-600 dark:text-${accent}-300 disabled:opacity-50`}
              >
                {unlinking ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </button>
              <p className={`text-xs text-${accent}-600 dark:text-${accent}-400 mb-1 flex items-center gap-1`}>
                <CheckCircle2 className="h-3 w-3" /> Currently linked
              </p>
              <p className={`font-medium text-${accent}-900 dark:text-${accent}-100 pr-6`}>
                {currentCampaignName || currentCampaignId}
              </p>
              <p className={`text-xs text-${accent}-600 dark:text-${accent}-400 mt-1`}>
                ID: {currentCampaignId}
              </p>
              {platform === 'tiktok' && smartPlusError && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-start gap-1">
                  ⚠️ This campaign may be deleted or inaccessible. Click the X to unlink it.
                </p>
              )}
              {platform === 'meta' && metaSync.status !== 'idle' && (
                <div className="mt-2 text-xs flex items-start gap-1.5">
                  {metaSync.status === 'checking' && (
                    <span className="text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <Loader className="h-3 w-3 animate-spin" /> Checking creatives…
                    </span>
                  )}
                  {metaSync.status === 'running' && (
                    <span className="text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <Loader className="h-3 w-3 animate-spin" /> Matching {metaSync.unmatchedBefore} unlinked
                      creative{metaSync.unmatchedBefore === 1 ? '' : 's'} by thumbnail…
                    </span>
                  )}
                  {metaSync.status === 'done' && (
                    <span className="text-green-700 dark:text-green-400">
                      ✓ Matched {metaSync.matched} of {metaSync.unmatchedBefore} creative
                      {metaSync.unmatchedBefore === 1 ? '' : 's'}
                      {metaSync.unmatchedAfter && metaSync.unmatchedAfter > 0
                        ? ` · ${metaSync.unmatchedAfter} still unlinked`
                        : ''}
                    </span>
                  )}
                  {metaSync.status === 'nothing-to-do' && (
                    <span className="text-gray-500 dark:text-gray-400">
                      All creatives already linked to Meta ads
                    </span>
                  )}
                  {metaSync.status === 'error' && (
                    <span className="text-amber-600 dark:text-amber-400">
                      ⚠ Sync failed: {metaSync.error}
                    </span>
                  )}
                </div>
              )}
              {platform === 'meta' && metaSync.debug && (
                <details className="mt-2 text-[10px]">
                  <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 select-none">
                    Sync debug ({metaSync.debug.meta_ads_total ?? 0} Meta ads /{' '}
                    {metaSync.debug.meta_ads_hashed ?? 0} hashed /{' '}
                    {metaSync.debug.local_creatives_total ?? 0} local /{' '}
                    {metaSync.debug.matches_total ?? 0} matched)
                  </summary>
                  <div className="mt-1 space-y-1 text-gray-600 dark:text-gray-300 font-mono">
                    <div>
                      <strong>Counts:</strong> meta_ads={metaSync.debug.meta_ads_total} · hashed=
                      {metaSync.debug.meta_ads_hashed} · local={metaSync.debug.local_creatives_total}{' '}
                      · phash_computed_now={metaSync.debug.local_phash_computed_now} · updated=
                      {metaSync.debug.updated_count}
                    </div>
                    <div>
                      <strong>By reason:</strong> video_id=
                      {metaSync.debug.matches_by_reason?.video_id} · image_hash=
                      {metaSync.debug.matches_by_reason?.image_hash} · phash=
                      {metaSync.debug.matches_by_reason?.phash}
                    </div>
                    {(() => {
                      const metaAds = (metaSync.debug?.unmatched_meta_ads ?? []) as any[];
                      const locals = (metaSync.debug?.unmatched_local_ad_creatives ?? []) as any[];
                      const autoMatchesCount = ((metaSync.debug?.matches as any[]) || []).length;
                      const existingCount = ((metaSync.debug?.existing_matches as any[]) || []).length;
                      // Hide the whole block only if there's literally nothing
                      // to show (no matches of any kind AND nothing unmatched).
                      if (
                        metaAds.length === 0 &&
                        locals.length === 0 &&
                        autoMatchesCount === 0 &&
                        existingCount === 0
                      ) {
                        return null;
                      }
                      const metaById = new Map(metaAds.map((a) => [a.ad_id, a]));
                      const localById = new Map(locals.map((l) => [l.id, l]));
                      const pairedMetaIds = new Set(manualPairs.map((p) => p.metaAdId));
                      const pairedLocalIds = new Set(manualPairs.map((p) => p.localId));
                      // hcti similarity at 64x64 is too noisy to trust below a
                      // confidence floor. When the best-match score is weak
                      // (nearest_distance > 20 → similarity < 80%), we treat
                      // the "nearest" suggestion as meaningless and don't use
                      // it for sort/alignment — the user matches visually.
                      const HIGH_CONF_MAX_DIST = 20; // percent_difference cutoff
                      const remainingMeta = metaAds
                        .filter(
                          (a) =>
                            !pairedMetaIds.has(a.ad_id) &&
                            !committedMetaAdIds.has(a.ad_id),
                        )
                        .slice()
                        .sort((a: any, b: any) => {
                          // If any score is high-confidence, use it for order;
                          // otherwise preserve original order (ad_name asc).
                          const da = a.nearest_distance ?? 999;
                          const db = b.nearest_distance ?? 999;
                          const aGood = da <= HIGH_CONF_MAX_DIST;
                          const bGood = db <= HIGH_CONF_MAX_DIST;
                          if (aGood && !bGood) return -1;
                          if (!aGood && bGood) return 1;
                          if (aGood && bGood) return da - db;
                          // Both noisy — stable order by ad_name numeric
                          const na = Number(a.ad_name) || 0;
                          const nb = Number(b.ad_name) || 0;
                          return na - nb;
                        });
                      const localsRemaining = locals.filter(
                        (l) => !pairedLocalIds.has(l.id),
                      );
                      // Only align locals to Meta rows when the suggestion is
                      // high-confidence. Otherwise list locals in stable id
                      // order — no fake alignment.
                      const localsMap = new Map(localsRemaining.map((l) => [l.id, l]));
                      const claimedLocals = new Set<string>();
                      const orderedLocals: any[] = [];
                      for (const m of remainingMeta) {
                        if ((m.nearest_distance ?? 999) > HIGH_CONF_MAX_DIST) continue;
                        const nid = (m as any).nearest_local_id;
                        if (nid && !claimedLocals.has(nid) && localsMap.has(nid)) {
                          orderedLocals.push(localsMap.get(nid));
                          claimedLocals.add(nid);
                        }
                      }
                      for (const l of localsRemaining) {
                        if (!claimedLocals.has(l.id)) orderedLocals.push(l);
                      }
                      const paddedMeta = remainingMeta;
                      const remainingLocals = orderedLocals;
                      const existingMatches =
                        (metaSync.debug?.existing_matches as any[]) || [];
                      return (
                        <div className="space-y-2 text-[11px]">
                          {(() => {
                            const autoMatches =
                              (metaSync.debug?.matches as any[]) || [];
                            if (autoMatches.length === 0) return null;
                            // Build lookup into locals-by-id and metas-by-id so
                            // we can render previews. The matched items no
                            // longer appear in unmatched_*, so we draw previews
                            // from the meta-previews bucket & local-previews
                            // bucket directly via known IDs.
                            return (
                              <div className="space-y-1 font-sans">
                                <div className="text-[10px] font-medium text-green-700 dark:text-green-400">
                                  ✓ Just auto-matched this sync ({autoMatches.length})
                                </div>
                                <ul className="space-y-1 max-h-40 overflow-y-auto">
                                  {autoMatches.map((m: any) => {
                                    const metaPreview = `https://wrlgoxbzlngdtomjhvnz.supabase.co/storage/v1/object/public/thumbnails/sync-previews/meta-v2/${m.meta_ad_id}.png`;
                                    const localPreview = `https://wrlgoxbzlngdtomjhvnz.supabase.co/storage/v1/object/public/thumbnails/sync-previews/local/${m.local_ad_creative_id}.png`;
                                    const sim =
                                      m.distance !== undefined
                                        ? (100 - m.distance).toFixed(1)
                                        : '?';
                                    return (
                                      <li
                                        key={`auto-${m.meta_ad_id}-${m.local_ad_creative_id}`}
                                        className="flex items-center gap-2 p-1.5 rounded border border-green-500 bg-green-50 dark:bg-green-900/20"
                                      >
                                        <img
                                          src={metaPreview}
                                          className="w-10 h-10 object-cover rounded shrink-0 border border-gray-300"
                                          alt=""
                                        />
                                        <div className="text-gray-500 text-[10px] shrink-0">
                                          #{m.meta_ad_name} ↔{' '}
                                          {String(m.local_ad_creative_id).slice(0, 8)}
                                        </div>
                                        <img
                                          src={localPreview}
                                          className="w-10 h-10 object-cover rounded shrink-0 border border-gray-300"
                                          alt=""
                                        />
                                        <div className="flex-1" />
                                        <span className="text-[10px] text-green-700 dark:text-green-300 font-medium">
                                          sim {sim}% ({m.reason})
                                        </span>
                                        <CheckCircle2 className="h-3 w-3 text-green-600 dark:text-green-400" />
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            );
                          })()}
                          {existingMatches.length > 0 && (
                            <div className="space-y-1 font-sans">
                              <div className="text-[10px] font-medium text-green-700 dark:text-green-400">
                                Already linked ({existingMatches.length})
                              </div>
                              <ul className="space-y-1 max-h-40 overflow-y-auto">
                                {existingMatches.map((em: any) => (
                                  <li
                                    key={`existing-${em.meta_ad_id}-${em.local_ad_creative_id}`}
                                    className="flex items-center gap-2 p-1.5 rounded border border-green-500 bg-green-50 dark:bg-green-900/20"
                                  >
                                    {em.meta_preview_url ? (
                                      <img
                                        src={em.meta_preview_url}
                                        className="w-10 h-10 object-cover rounded shrink-0 border border-gray-300"
                                        alt=""
                                      />
                                    ) : (
                                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                                    )}
                                    <div className="text-gray-500 text-[10px] shrink-0">
                                      #{em.meta_ad_name || '?'} ↔{' '}
                                      {String(em.local_ad_creative_id).slice(0, 8)}
                                    </div>
                                    {em.local_preview_url ? (
                                      <img
                                        src={em.local_preview_url}
                                        className="w-10 h-10 object-cover rounded shrink-0 border border-gray-300"
                                        alt=""
                                      />
                                    ) : (
                                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                                    )}
                                    <div className="flex-1" />
                                    {em.percent_difference !== undefined && (
                                      <span className="text-[10px] text-gray-500 font-mono">
                                        sim {(100 - em.percent_difference).toFixed(1)}%
                                      </span>
                                    )}
                                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" /> Linked
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(remainingMeta.length > 0 || localsRemaining.length > 0) && (
                            <div className="text-gray-600 dark:text-gray-300 font-sans">
                              <strong>Manual matcher</strong> — drag a Meta ad
                              from the left column onto a local creative on the
                              right. The pair is saved to the database on drop.
                            </div>
                          )}
                          {manualPairs.length > 0 && (
                            <div className="space-y-1 font-sans">
                              <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300">
                                Proposed pairs ({manualPairs.length})
                              </div>
                              <ul className="space-y-1">
                                {manualPairs.map(({ metaAdId, localId }) => {
                                  const m = metaById.get(metaAdId);
                                  const l = localById.get(localId);
                                  const isCommitted = committedMetaAdIds.has(metaAdId);
                                  const isCommitting = committingMetaAdId === metaAdId;
                                  return (
                                    <li
                                      key={`pair-${metaAdId}-${localId}`}
                                      className={`flex items-center gap-2 p-1.5 rounded border ${
                                        isCommitted
                                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                          : 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                                      }`}
                                    >
                                      {m?.preview_data_url ? (
                                        <img
                                          src={m.preview_data_url}
                                          className="w-10 h-10 object-cover rounded shrink-0 border border-gray-300"
                                          alt=""
                                        />
                                      ) : (
                                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                                      )}
                                      <div className="text-gray-500 text-[10px] shrink-0">
                                        #{m?.ad_name || '?'} ↔ {String(localId).slice(0, 8)}
                                      </div>
                                      {l?.preview_data_url ? (
                                        <img
                                          src={l.preview_data_url}
                                          className="w-10 h-10 object-cover rounded shrink-0 border border-gray-300"
                                          alt=""
                                        />
                                      ) : (
                                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                                      )}
                                      <div className="flex-1" />
                                      {isCommitted ? (
                                        <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                          <CheckCircle2 className="h-3 w-3" /> Linked
                                        </span>
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              commitMetaCreativeLink(metaAdId, localId)
                                            }
                                            disabled={isCommitting}
                                            title="Confirm & save link"
                                            className="p-1 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                                          >
                                            {isCommitting ? (
                                              <Loader className="h-3 w-3 animate-spin" />
                                            ) : (
                                              <Check className="h-3 w-3" />
                                            )}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setManualPairs((prev) =>
                                                prev.filter(
                                                  (p) =>
                                                    !(
                                                      p.metaAdId === metaAdId &&
                                                      p.localId === localId
                                                    ),
                                                ),
                                              )
                                            }
                                            disabled={isCommitting}
                                            title="Cancel pairing"
                                            className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-2 font-sans">
                            <div>
                              <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1">
                                Meta ads ({remainingMeta.length}) — drag → · sorted by best match
                              </div>
                              <ul className="max-h-80 overflow-y-auto space-y-1 pr-1">
                                {paddedMeta.map((a) => (
                                  <li
                                    key={a.ad_id}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('text/plain', a.ad_id);
                                      e.dataTransfer.effectAllowed = 'move';
                                      setDragMetaAdId(a.ad_id);
                                      console.log('[meta-sync] drag start', {
                                        meta_ad_id: a.ad_id,
                                        ad_name: a.ad_name,
                                      });
                                    }}
                                    onDragEnd={() => {
                                      console.log('[meta-sync] drag end', { meta_ad_id: a.ad_id });
                                      setDragMetaAdId(null);
                                    }}
                                    className={`flex items-center gap-2 p-1.5 rounded border cursor-grab active:cursor-grabbing ${
                                      dragMetaAdId === a.ad_id
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 hover:border-blue-400'
                                    }`}
                                  >
                                    {a.preview_data_url || a.preview_url ? (
                                      <img
                                        src={a.preview_data_url || a.preview_url}
                                        className="w-12 h-12 object-cover rounded shrink-0"
                                        alt=""
                                      />
                                    ) : (
                                      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                                    )}
                                    {/* Inline best-match local preview — shows the
                                         suggested pair for this Meta ad regardless
                                         of column alignment. */}
                                    {(() => {
                                      if (!a.nearest_local_id) return null;
                                      const nearestLocal = (
                                        metaSync.debug?.unmatched_local_ad_creatives as any[]
                                      )?.find(
                                        (l: any) =>
                                          l.id === a.nearest_local_id,
                                      );
                                      const src =
                                        nearestLocal?.preview_url ||
                                        nearestLocal?.preview_data_url;
                                      if (!src) return null;
                                      const dist = a.nearest_distance ?? 999;
                                      const borderCls =
                                        dist <= 10
                                          ? 'border-green-500'
                                          : dist <= 20
                                            ? 'border-amber-400'
                                            : 'border-gray-400';
                                      return (
                                        <>
                                          <span className="text-gray-400 shrink-0">
                                            →
                                          </span>
                                          <img
                                            src={src}
                                            title={`Best match: ${a.nearest_local_id} (sim ${(100 - dist).toFixed(1)}%)`}
                                            className={`w-12 h-12 object-cover rounded shrink-0 border-2 ${borderCls}`}
                                            alt=""
                                          />
                                        </>
                                      );
                                    })()}
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate">
                                        <strong>#{a.ad_name || '?'}</strong>{' '}
                                        <span className="text-gray-500">
                                          {String(a.ad_id).slice(-6)}
                                        </span>
                                      </div>
                                      <div className="text-[10px] truncate">
                                        {a.nearest_distance !== undefined ? (
                                          <>
                                            <span
                                              className={
                                                a.nearest_distance <= 5
                                                  ? 'text-green-600 dark:text-green-400 font-medium'
                                                  : a.nearest_distance <= 20
                                                    ? 'text-amber-600 dark:text-amber-400'
                                                    : 'text-gray-400'
                                              }
                                              title={`hcti percent difference (lower = more similar)`}
                                            >
                                              {a.nearest_distance > 20
                                                ? 'no reliable match'
                                                : `sim ${(100 - a.nearest_distance).toFixed(1)}% → ${String(a.nearest_local_id).slice(-6)}`}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="text-gray-500">no sim</span>
                                        )}
                                        {a.thumbnail_url ? (
                                          <>
                                            <span className="text-gray-400">{' · '}</span>
                                            <a
                                              href={a.thumbnail_url}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="underline text-blue-600"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              orig
                                            </a>
                                          </>
                                        ) : null}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                                {remainingMeta.length === 0 && (
                                  <li className="text-gray-400 text-[10px] italic p-2">
                                    All Meta ads linked
                                  </li>
                                )}
                              </ul>
                            </div>
                            <div>
                              <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1">
                                Local creatives ({localsRemaining.length}) — drop here · aligned to best match
                              </div>
                              <ul className="max-h-80 overflow-y-auto space-y-1 pl-1">
                                {remainingLocals.map((l) => (
                                  <li
                                    key={l.id}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const metaAdId = e.dataTransfer.getData('text/plain');
                                      if (!metaAdId) {
                                        console.warn('[meta-sync] drop without meta_ad_id');
                                        return;
                                      }
                                      console.log('[meta-sync] drop', {
                                        meta_ad_id: metaAdId,
                                        local_id: l.id,
                                      });
                                      // Register the pair in state so it shows
                                      // in the "Proposed pairs" list, then
                                      // immediately commit to DB — no need for
                                      // an explicit checkmark click.
                                      setManualPairs((prev) => [
                                        ...prev.filter(
                                          (p) =>
                                            p.metaAdId !== metaAdId &&
                                            p.localId !== l.id,
                                        ),
                                        { metaAdId, localId: l.id },
                                      ]);
                                      setDragMetaAdId(null);
                                      commitMetaCreativeLink(metaAdId, l.id);
                                    }}
                                    className={`flex items-center gap-2 p-1.5 rounded border transition-colors ${
                                      dragMetaAdId
                                        ? 'border-dashed border-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30'
                                    }`}
                                  >
                                    {l.preview_data_url || l.preview_url ? (
                                      <img
                                        src={l.preview_data_url || l.preview_url}
                                        className="w-12 h-12 object-cover rounded shrink-0"
                                        alt=""
                                      />
                                    ) : (
                                      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-gray-500">
                                        {String(l.id).slice(-8)}
                                      </div>
                                      <div className="text-gray-500 text-[10px] truncate">
                                        {l.thumbnail_url ? (
                                          <a
                                            href={l.thumbnail_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline text-blue-600"
                                          >
                                            orig
                                          </a>
                                        ) : (
                                          'no-thumb'
                                        )}
                                        {l.dimensions ? ` · ${l.dimensions}` : ''}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                                {localsRemaining.length === 0 && (
                                  <li className="text-gray-400 text-[10px] italic p-2">
                                    All locals paired
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <details>
                      <summary className="cursor-pointer">Raw JSON (preview data URLs stripped)</summary>
                      <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-900/40 rounded overflow-x-auto text-[9px] max-h-60">
                        {JSON.stringify(
                          metaSync.debug,
                          (k, v) =>
                            k === 'preview_data_url' && typeof v === 'string'
                              ? `[data:image/png;base64,…${v.length}b]`
                              : v,
                          2,
                        )}
                      </pre>
                    </details>
                  </div>
                </details>
              )}
            </div>
          )}

          {/* TikTok Smart+ Ads preview when campaign is linked */}
          {platform === 'tiktok' && currentCampaignId && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm font-medium dark:text-white">Smart+ Ads in this campaign</p>
                {loadingSmartPlus && <Loader className="h-3 w-3 animate-spin text-gray-400" />}
              </div>
              <div className="p-3 max-h-72 overflow-y-auto">
                {smartPlusError ? (
                  <p className="text-xs text-red-600 dark:text-red-400">{smartPlusError}</p>
                ) : smartPlusAds === null ? null : smartPlusAds.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    No Smart+ ads in this campaign.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {smartPlusAds.map((ad: any) => (
                      <li
                        key={ad.smart_plus_ad_id}
                        className="bg-gray-50 dark:bg-gray-900/40 rounded p-2"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            {ad.operation_status === 'ENABLE' ? (
                              <Power className="h-3 w-3 text-green-500 shrink-0" />
                            ) : (
                              <PowerOff className="h-3 w-3 text-gray-400 shrink-0" />
                            )}
                            <span
                              className="text-xs font-medium dark:text-white truncate"
                              title={ad.ad_name}
                            >
                              {ad.ad_name || ad.smart_plus_ad_id}
                            </span>
                          </div>
                          <span className="text-[10px] text-gray-400 shrink-0">
                            {ad.creative_list?.length || 0} creative
                            {(ad.creative_list?.length || 0) === 1 ? '' : 's'}
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-400 mb-2">
                          ID: {ad.smart_plus_ad_id} · {ad.secondary_status}
                        </p>
                        {ad.creative_list && ad.creative_list.length > 0 && (
                          <ul className="space-y-1 pl-1 border-l border-gray-200 dark:border-gray-700">
                            {ad.creative_list.map((c: any, idx: number) => (
                              <li
                                key={c.ad_material_id || idx}
                                className="pl-2 text-[11px] flex items-start gap-2"
                              >
                                {c.material_operation_status === 'ENABLE' ? (
                                  <Video className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                                ) : (
                                  <Video className="h-3 w-3 text-gray-400 mt-0.5 shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <p className="dark:text-gray-200 truncate">
                                    {c.matched_creative?.video_name ||
                                      c.material_name ||
                                      (c.tiktok_item_id
                                        ? `Spark Ad · item ${c.tiktok_item_id}`
                                        : c.video_id
                                        ? `Video ${c.video_id}`
                                        : c.ad_format || 'Creative')}
                                  </p>
                                  {c.matched_creative ? (
                                    <p className="text-[10px] text-green-600 dark:text-green-400 truncate">
                                      {c.matched_creative.artist_name
                                        ? `${c.matched_creative.artist_name} · `
                                        : ''}
                                      auth: {c.matched_creative.auth_code?.slice(0, 12) || '—'}
                                      {c.matched_creative.auth_code &&
                                      c.matched_creative.auth_code.length > 12
                                        ? '…'
                                        : ''}
                                    </p>
                                  ) : (
                                    <p className="text-[10px] text-amber-600 dark:text-amber-400">
                                      Not linked to any internal ad_creative
                                    </p>
                                  )}
                                  <p className="text-gray-400 text-[10px]">
                                    material: {c.ad_material_id} · {c.material_operation_status}
                                    {c.identity_type ? ` · ${c.identity_type}` : ''}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${label} campaigns...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-2">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : campaigns.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {campaigns.map((campaign, idx) => (
                <button
                  key={campaign.campaign_id || `campaign-${idx}`}
                  onClick={() => setSelectedCampaign(campaign)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedCampaign?.campaign_id === campaign.campaign_id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <p className="font-medium dark:text-white">{campaign.campaign_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <span>{campaign.objective || ''}</span>
                    {(() => {
                      const raw = (campaign.effective_status || campaign.status || '').toUpperCase();
                      if (!raw) return null;
                      const isActive = raw === 'ENABLE' || raw === 'ACTIVE';
                      const label = isActive ? 'Active' : 'Disabled';
                      const cls = isActive
                        ? 'text-green-600 dark:text-green-400 font-medium'
                        : 'text-red-600 dark:text-red-400 font-medium';
                      return (
                        <>
                          <span>•</span>
                          <span className={cls}>{label}</span>
                        </>
                      );
                    })()}
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                    ID: {campaign.campaign_id}
                  </p>
                </button>
              ))}
            </div>
          ) : searchQuery.trim().length >= 2 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              No campaigns found
            </p>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              Type at least 2 characters to search
            </p>
          )}

          {/* TikTok quick-create when no matching campaign exists */}
          {platform === 'tiktok' &&
            !currentCampaignId &&
            !loading &&
            campaigns.length === 0 &&
            searchQuery.trim().length >= 2 && (
              <div className="mt-2 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Targeting
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tiktok-link-targeting"
                        value="dach"
                        checked={tiktokTargeting === 'dach'}
                        onChange={() => setTiktokTargeting('dach')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm dark:text-white">DACH</span>
                      <span className="text-xs text-gray-500">(DE, AT, CH)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tiktok-link-targeting"
                        value="international"
                        checked={tiktokTargeting === 'international'}
                        onChange={() => setTiktokTargeting('international')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm dark:text-white">International</span>
                      <span className="text-xs text-gray-500">(30+ countries)</span>
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCreateTikTokCampaign}
                  disabled={creatingTiktokCampaign}
                  className="w-full px-4 py-2 bg-black hover:bg-gray-900 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2 border border-gray-700"
                >
                  {creatingTiktokCampaign ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  )}
                  {creatingTiktokCampaign
                    ? 'Erstelle Kampagne...'
                    : 'TikTok Smart+ Kampagne erstellen'}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Erstellt eine pausierte Smart+ Kampagne: 3 Tage, 20€/Tag, 60€ Lifetime Budget
                </p>

                {/* song.so smartlink preview — will be used as landing page */}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    song.so Smartlink (Landing Page)
                  </label>

                  {/* Effective URL: manual override wins, else auto-detected */}
                  {manualSmartlinkUrl ? (
                    <div className="flex items-center justify-between gap-2">
                      <a
                        href={manualSmartlinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
                      >
                        {manualSmartlinkUrl}
                      </a>
                      <button
                        type="button"
                        onClick={() => setManualSmartlinkUrl(null)}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 shrink-0"
                      >
                        Clear
                      </button>
                    </div>
                  ) : smartlinkLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Loader className="h-3 w-3 animate-spin" />
                      Suche in song.so…
                    </div>
                  ) : smartlinkUrl ? (
                    <a
                      href={smartlinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
                    >
                      {smartlinkUrl}
                    </a>
                  ) : smartlinkChecked ? (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Kein Smartlink in song.so gefunden. Kampagne wird ohne Landing Page erstellt.
                    </p>
                  ) : null}

                  {/* Manual search field */}
                  <div className="mt-2 relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                    <input
                      type="text"
                      value={smartlinkSearchQuery}
                      onChange={(e) => setSmartlinkSearchQuery(e.target.value)}
                      placeholder="In song.so suchen (Artist, Titel, Slug)…"
                      className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {smartlinkSearching && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
                      <Loader className="h-3 w-3 animate-spin" />
                      Suche…
                    </div>
                  )}

                  {!smartlinkSearching &&
                    smartlinkSearchQuery.trim().length >= 2 &&
                    smartlinkSearchResults.length > 0 && (
                      <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                        {smartlinkSearchResults.map((r) => {
                          const isSelected = manualSmartlinkUrl === r.smartlink_url;
                          return (
                            <li key={r.smartlink_id}>
                              <button
                                type="button"
                                onClick={() => setManualSmartlinkUrl(r.smartlink_url)}
                                className={`w-full text-left px-2 py-1.5 text-xs transition-colors ${
                                  isSelected
                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200'
                                }`}
                              >
                                <div className="font-medium truncate">
                                  {r.artist_name} — {r.release_name || r.slug}
                                </div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                  {r.smartlink_url}
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                  {!smartlinkSearching &&
                    smartlinkSearchQuery.trim().length >= 2 &&
                    smartlinkSearchResults.length === 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Keine Treffer
                      </p>
                    )}
                </div>
              </div>
            )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedCampaign || saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </motion.div>
    </div>
  );
}
